<template>
  <div ref="containerRef" class="canvas-container position-relative">
    <!-- Loading Spinner -->
    <div v-if="isImageLoading" class="loading-overlay">
      <div class="spinner-border text-warning" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
      <p class="loading-text mt-3">Image Loading...</p>
    </div>

    <!-- Image and Canvas (always rendered, but hidden while loading) -->
    <img
      ref="imageRef"
      :src="imageUrl"
      :style="imageStyle"
      class="base-image"
      :class="{ 'image-hidden': isImageLoading }"
      @load="handleImageLoad"
      @error="handleImageError"
      alt="Annotation image"
    />
    <canvas
      ref="canvasRef"
      class="annotation-canvas"
      :class="{ 'image-hidden': isImageLoading }"
      :style="canvasStyle"
      @mousedown="handleMouseDown"
      @mousemove="handleMouseMove"
      @mouseup="handleMouseUp"
      @mouseleave="handleMouseLeave"
    />
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount, nextTick } from 'vue'
import { useStore } from 'vuex'
import { CanvasDrawingManager } from '@/helpers/canvasDrawingManager'

const props = defineProps({
  imageUrl: {
    type: String,
    required: true
  },
  imageId: {
    type: String,
    required: true
  },
  imageFilter: {
    type: String,
    default: 'none'
  },
  currentImage: {
    type: Object,
    default: null
  }
})

// Get actual image dimensions from metadata (not PNG dimensions)
const getActualImageDimensions = () => {
  if (!props.currentImage) return null

  // Try to get from metadata "Dimensions" field (format: "3584x4608")
  const metadata = props.currentImage.metadata?.[0]?.values || []
  const dimensionsField = metadata.find(m => m.name === 'Dimensions')
  if (dimensionsField?.value) {
    const [width, height] = dimensionsField.value.split('x').map(Number)
    if (width && height) {
      return { width, height }
    }
  }

  // Fallback: try to get from dicom metadata or other sources
  return null
}

const store = useStore()
const containerRef = ref(null)
const imageRef = ref(null)
const canvasRef = ref(null)
const drawingManager = ref(null)
const currentAnchor = ref(null)
const isImageLoading = ref(true)
const resizeObserver = ref(null)

const drawingMode = computed(() => store.state.labelerAnnotation.drawingMode)
const annotations = computed(() => store.state.labelerAnnotation.annotations)
const editingAnnotationId = computed(() => store.state.labelerAnnotation.editingAnnotationId)
const isEditMode = computed(() => store.state.labelerAnnotation.isEditMode)

const canvasStyle = computed(() => ({
  cursor: drawingMode.value ? 'crosshair' : 'default'
}))

const imageStyle = computed(() => ({
  filter: props.imageFilter
}))

const handleImageLoad = () => {
  const img = imageRef.value
  const canvas = canvasRef.value

  if (!img || !canvas) return

  canvas.width = img.clientWidth
  canvas.height = img.clientHeight

  // Use ACTUAL image dimensions from metadata, not PNG naturalWidth/naturalHeight
  // This ensures coords are scaled to the original DICOM dimensions
  const actualDimensions = getActualImageDimensions()
  const originalWidth = actualDimensions?.width || img.naturalWidth
  const originalHeight = actualDimensions?.height || img.naturalHeight

  console.log('AnnotationCanvas dimensions:', {
    pngNatural: { w: img.naturalWidth, h: img.naturalHeight },
    actualFromMetadata: actualDimensions,
    usingOriginal: { w: originalWidth, h: originalHeight },
    display: { w: img.clientWidth, h: img.clientHeight }
  })

  drawingManager.value = new CanvasDrawingManager(
    canvas,
    canvas.getContext('2d'),
    originalWidth,
    originalHeight,
    img.clientWidth,
    img.clientHeight
  )

  renderAnnotations()
  isImageLoading.value = false
}

const handleImageError = () => {
  console.error('Failed to load image')
  isImageLoading.value = false
}

// Update canvas dimensions when layout changes (e.g., panel collapse/expand)
const updateCanvasDimensions = () => {
  const img = imageRef.value
  const canvas = canvasRef.value

  if (!img || !canvas || !drawingManager.value) return

  // Only update if dimensions actually changed
  const newWidth = img.clientWidth
  const newHeight = img.clientHeight

  if (canvas.width !== newWidth || canvas.height !== newHeight) {
    canvas.width = newWidth
    canvas.height = newHeight

    // Use ACTUAL image dimensions from metadata
    const actualDimensions = getActualImageDimensions()
    const originalWidth = actualDimensions?.width || img.naturalWidth
    const originalHeight = actualDimensions?.height || img.naturalHeight

    drawingManager.value.updateDimensions(
      originalWidth,
      originalHeight,
      newWidth,
      newHeight
    )

    renderAnnotations()
  }
}

const handleMouseDown = (e) => {
  if (!drawingManager.value) return

  const rect = canvasRef.value.getBoundingClientRect()
  const point = {
    x: e.clientX - rect.left,
    y: e.clientY - rect.top
  }

  // Check if in edit mode
  if (isEditMode.value && editingAnnotationId.value) {
    const annotation = annotations.value.find(a => a.id === editingAnnotationId.value)
    if (annotation) {
      // Check if clicking on an anchor
      const anchor = drawingManager.value.getAnchorAtPoint(point, annotation)
      if (anchor) {
        console.log('Start dragging anchor:', anchor)
        currentAnchor.value = anchor
        drawingManager.value.isDraggingAnchor = true
        drawingManager.value.draggedAnchorIndex = anchor.index || anchor.key
        return
      }
    }
  }

  // Normal drawing mode
  if (drawingMode.value) {
    console.log('Starting drawing:', { mode: drawingMode.value, point })
    drawingManager.value.startDrawing(point, drawingMode.value)
  }
}

const handleMouseMove = (e) => {
  if (!drawingManager.value) return

  const rect = canvasRef.value.getBoundingClientRect()
  const point = {
    x: e.clientX - rect.left,
    y: e.clientY - rect.top
  }

  // Handle anchor dragging in edit mode
  if (drawingManager.value.isDraggingAnchor && currentAnchor.value) {
    const annotation = annotations.value.find(a => a.id === editingAnnotationId.value)
    if (annotation) {
      const anchorKey = currentAnchor.value.key || currentAnchor.value.index
      const updatedAnnotation = drawingManager.value.updateAnnotationFromAnchor(
        annotation,
        anchorKey,
        point
      )

      // Update annotation in store
      store.dispatch('labelerAnnotation/updateAnnotation', {
        annotationId: annotation.id,
        updates: updatedAnnotation
      })

      renderAnnotations()
    }
    return
  }

  // Normal drawing mode
  if (drawingManager.value.isDrawing) {
    drawingManager.value.continueDrawing(point)
  }
}

const handleMouseUp = (e) => {
  if (!drawingManager.value) return

  // Stop anchor dragging
  if (drawingManager.value.isDraggingAnchor) {
    drawingManager.value.isDraggingAnchor = false
    currentAnchor.value = null
    renderAnnotations()
    return
  }

  // Normal drawing mode
  if (drawingManager.value.isDrawing) {
    const rect = canvasRef.value.getBoundingClientRect()
    const point = {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    }

    const annotation = drawingManager.value.finishDrawing(point)

    if (annotation) {
      store.dispatch('labelerAnnotation/finishDrawing', annotation)
      renderAnnotations()
    }
  }
}

const handleMouseLeave = () => {
  if (drawingManager.value && drawingManager.value.isDrawing) {
    drawingManager.value.isDrawing = false
    drawingManager.value.clearPreview()
  }
}

const renderAnnotations = () => {
  if (!drawingManager.value) return
  // Pass editingAnnotationId to hide other annotations when editing
  drawingManager.value.renderAnnotations(
    annotations.value,
    isEditMode.value ? editingAnnotationId.value : null
  )

  // Draw anchors for editing annotation
  if (isEditMode.value && editingAnnotationId.value) {
    const annotation = annotations.value.find(a => a.id === editingAnnotationId.value)
    if (annotation) {
      drawingManager.value.drawAnchors(annotation)
    }
  }
}

// Watch for annotation changes
watch(annotations, () => {
  renderAnnotations()
}, { deep: true })

// Watch for edit mode changes to immediately show/hide anchors
watch([isEditMode, editingAnnotationId], () => {
  renderAnnotations()
})

// Watch for image URL changes
watch(() => props.imageUrl, () => {
  isImageLoading.value = true
  if (drawingManager.value) {
    drawingManager.value.clearCanvas()
  }
})

// Watch for panel collapse/expand to trigger dimension recalculation
const leftPanelCollapsed = computed(() => store.state.workspacePanel.leftPanelCollapsed)
const rightPanelCollapsed = computed(() => store.state.workspacePanel.rightPanelCollapsed)

watch([leftPanelCollapsed, rightPanelCollapsed], () => {
  // Use nextTick to wait for CSS layout to update
  nextTick(() => {
    // Small delay to ensure CSS transition completes
    setTimeout(updateCanvasDimensions, 50)
  })
})

onMounted(() => {
  // Handle window resize
  window.addEventListener('resize', handleImageLoad)

  // Set up ResizeObserver to detect layout changes
  resizeObserver.value = new ResizeObserver(() => {
    updateCanvasDimensions()
  })

  if (containerRef.value) {
    resizeObserver.value.observe(containerRef.value)
  }
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', handleImageLoad)

  if (resizeObserver.value) {
    resizeObserver.value.disconnect()
  }
})
</script>

<style scoped>
.canvas-container {
  width: 100%;
  height: 100%;
  position: relative;
  overflow: hidden;
  user-select: none;
  min-height: 400px;
}

.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-color: var(--color-bg-primary);
  z-index: 20;
}

.loading-text {
  color: #f0b93b;
  font-size: 1.2rem;
  font-weight: 700;
  letter-spacing: 0.05em;
}

.spinner-border {
  width: 3rem;
  height: 3rem;
  border-width: 0.3rem;
}

.base-image {
  width: 100%;
  height: auto;
  display: block;
  user-select: none;
  pointer-events: none;
}

.annotation-canvas {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: auto;
  pointer-events: all;
  z-index: 10;
}

.image-hidden {
  opacity: 0;
  pointer-events: none;
}
</style>
